import { useEffect, useRef, useState } from "react";

const rooms = [
  {
    id: "battery",
    icon: "ri-battery-2-charge-line",
    title: "Batarya Odası",
    subtitle: "BATTERY ROOM",
    desc: "LFP batarya modülleri, BMS entegrasyonu ve yangın bölmelendirmesi ile donatılmış ana depolama alanı.",
    specs: ["LFP Hücre Teknolojisi", "Aktif BMS Sistemi", "Yangın Bölmelendirme"],
    color: "#A66A3F",
    bg: "#FFF8F4",
    area: "~72 m²",
  },
  {
    id: "pcs",
    icon: "ri-cpu-line",
    title: "PCS Odası",
    subtitle: "POWER CONVERSION",
    desc: "Güç dönüştürücü sistemleri, trafo ve şebeke bağlantı ekipmanları. AC/DC dönüşüm merkezi.",
    specs: ["AC/DC Dönüştürücü", "Trafo Ünitesi", "Şebeke Bağlantısı"],
    color: "#2B2B2B",
    bg: "#F7F7F7",
    area: "~18 m²",
  },
  {
    id: "scada",
    icon: "ri-computer-line",
    title: "SCADA & Kontrol",
    subtitle: "CONTROL ROOM",
    desc: "Merkezi izleme, uzaktan kontrol, veri kaydı ve alarm yönetimi. 7/24 operasyon desteği.",
    specs: ["Uzaktan İzleme", "Alarm Yönetimi", "Veri Kaydı"],
    color: "#2B2B2B",
    bg: "#F7F7F7",
    area: "~12 m²",
  },
  {
    id: "mech",
    icon: "ri-tools-line",
    title: "Mekanik Oda",
    subtitle: "MECHANICAL ROOM",
    desc: "HVAC ekipmanları, yangın söndürme sistemleri ve bakım erişim alanı.",
    specs: ["HVAC Sistemi", "Yangın Söndürme", "Bakım Erişimi"],
    color: "#2B2B2B",
    bg: "#F7F7F7",
    area: "~18 m²",
  },
];

export default function LayoutSection() {
  const sectionRef = useRef<HTMLDivElement>(null);
  const [activeRoom, setActiveRoom] = useState<string>("battery");
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setVisible(true);
          }
        });
      },
      { threshold: 0.1 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  const activeData = rooms.find((r) => r.id === activeRoom) ?? rooms[0];

  return (
    <section id="ic-yerlesim" ref={sectionRef} className="py-24 md:py-32 bg-white">
      <div className="max-w-[1200px] mx-auto px-6">

        {/* Header */}
        <div
          className="mb-14"
          style={{
            opacity: visible ? 1 : 0,
            transform: visible ? "translateY(0)" : "translateY(20px)",
            transition: "opacity 0.7s ease, transform 0.7s ease",
          }}
        >
          <div className="flex items-center gap-3 mb-3">
            <div className="w-8 h-px bg-[#A66A3F]" />
            <span className="text-[#A66A3F] text-[11px] font-semibold tracking-[0.2em] uppercase">
              Mekan Organizasyonu
            </span>
          </div>
          <h2 className="text-3xl md:text-4xl font-bold text-[#2B2B2B]">
            İç Yerleşim Planı
          </h2>
          <p className="text-[#6A6A6A] text-sm mt-3 max-w-[480px] leading-relaxed">
            Prefabrike yapı içindeki her alan, maksimum verimlilik ve güvenlik için mühendislik odaklı tasarlanmıştır.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16 items-start">

          {/* LEFT — Floor Plan Visual */}
          <div
            style={{
              opacity: visible ? 1 : 0,
              transform: visible ? "translateX(0)" : "translateX(-24px)",
              transition: "opacity 0.8s ease 0.2s, transform 0.8s ease 0.2s",
            }}
          >
            {/* Plan label */}
            <div className="flex items-center justify-between mb-4">
              <span className="text-[10px] text-[#A66A3F] font-semibold tracking-[0.18em] uppercase">
                Plan Görünümü — Tip A
              </span>
              <span className="text-[10px] text-[#9A9A9A] font-mono">20m × 6m × 3.2m</span>
            </div>

            {/* Building plan */}
            <div className="border-2 border-[#2B2B2B] relative bg-white">

              {/* North label */}
              <div className="absolute -top-5 left-1/2 -translate-x-1/2 text-[9px] text-[#9A9A9A] tracking-widest uppercase font-mono">
                KUZEY
              </div>

              {/* Main floor grid */}
              <div className="flex h-[320px] md:h-[360px]">

                {/* BATTERY ROOM — large left section */}
                <button
                  onClick={() => setActiveRoom("battery")}
                  className="relative flex-[3] border-r-2 border-[#2B2B2B] flex flex-col items-center justify-center cursor-pointer transition-all duration-200 group"
                  style={{
                    background: activeRoom === "battery" ? "#FFF0E6" : "#FAFAFA",
                  }}
                >
                  <div
                    className="absolute inset-0 border-2 border-[#A66A3F] pointer-events-none transition-opacity duration-200"
                    style={{ opacity: activeRoom === "battery" ? 1 : 0 }}
                  />
                  {/* Hatch pattern for battery */}
                  <div className="absolute inset-0 opacity-[0.04]"
                    style={{
                      backgroundImage: "repeating-linear-gradient(45deg, #A66A3F 0, #A66A3F 1px, transparent 0, transparent 50%)",
                      backgroundSize: "8px 8px",
                    }}
                  />
                  <i className="ri-battery-2-charge-line text-2xl text-[#A66A3F] mb-2 relative z-10" />
                  <span className="text-[11px] font-bold text-[#2B2B2B] tracking-wider uppercase relative z-10">Batarya</span>
                  <span className="text-[10px] text-[#A66A3F] font-mono mt-1 relative z-10">~72 m²</span>
                  {/* Zone labels */}
                  <div className="absolute top-2 left-2 flex flex-col gap-1">
                    {["A1","A2","A3","A4"].map(z => (
                      <span key={z} className="text-[8px] font-mono text-[#A66A3F]/60 bg-[#A66A3F]/8 px-1">{z}</span>
                    ))}
                  </div>
                </button>

                {/* RIGHT COLUMN — 3 stacked rooms */}
                <div className="flex-[2] flex flex-col">

                  {/* PCS */}
                  <button
                    onClick={() => setActiveRoom("pcs")}
                    className="relative flex-1 border-b-2 border-[#2B2B2B] flex flex-col items-center justify-center cursor-pointer transition-all duration-200"
                    style={{
                      background: activeRoom === "pcs" ? "#F0F0F0" : "#F7F7F7",
                    }}
                  >
                    <div
                      className="absolute inset-0 border-2 border-[#2B2B2B] pointer-events-none transition-opacity duration-200"
                      style={{ opacity: activeRoom === "pcs" ? 1 : 0 }}
                    />
                    <i className="ri-cpu-line text-lg text-[#2B2B2B] mb-1" />
                    <span className="text-[10px] font-bold text-[#2B2B2B] tracking-wider uppercase">PCS</span>
                    <span className="text-[9px] text-[#9A9A9A] font-mono mt-0.5">B1</span>
                  </button>

                  {/* SCADA */}
                  <button
                    onClick={() => setActiveRoom("scada")}
                    className="relative flex-1 border-b-2 border-[#2B2B2B] flex flex-col items-center justify-center cursor-pointer transition-all duration-200"
                    style={{
                      background: activeRoom === "scada" ? "#F0F0F0" : "#F7F7F7",
                    }}
                  >
                    <div
                      className="absolute inset-0 border-2 border-[#2B2B2B] pointer-events-none transition-opacity duration-200"
                      style={{ opacity: activeRoom === "scada" ? 1 : 0 }}
                    />
                    <i className="ri-computer-line text-lg text-[#2B2B2B] mb-1" />
                    <span className="text-[10px] font-bold text-[#2B2B2B] tracking-wider uppercase">SCADA</span>
                    <span className="text-[9px] text-[#9A9A9A] font-mono mt-0.5">C1</span>
                  </button>

                  {/* MECHANICAL */}
                  <button
                    onClick={() => setActiveRoom("mech")}
                    className="relative flex-1 flex flex-col items-center justify-center cursor-pointer transition-all duration-200"
                    style={{
                      background: activeRoom === "mech" ? "#F0F0F0" : "#F7F7F7",
                    }}
                  >
                    <div
                      className="absolute inset-0 border-2 border-[#2B2B2B] pointer-events-none transition-opacity duration-200"
                      style={{ opacity: activeRoom === "mech" ? 1 : 0 }}
                    />
                    <i className="ri-tools-line text-lg text-[#2B2B2B] mb-1" />
                    <span className="text-[10px] font-bold text-[#2B2B2B] tracking-wider uppercase">MEKANİK</span>
                    <span className="text-[9px] text-[#9A9A9A] font-mono mt-0.5">D1</span>
                  </button>

                </div>
              </div>

              {/* Door indicator */}
              <div className="absolute bottom-0 left-1/2 -translate-x-1/2 translate-y-[1px]">
                <div className="w-8 h-1 bg-white border-x-2 border-[#2B2B2B]" />
                <div className="text-[8px] text-[#9A9A9A] text-center mt-0.5 tracking-widest uppercase font-mono">GİRİŞ</div>
              </div>
            </div>

            {/* Dimensions bar */}
            <div className="mt-6 grid grid-cols-3 gap-0 border border-[#E8E8E8]">
              {[
                { label: "Uzunluk", val: "14.0 m" },
                { label: "Genişlik", val: "3.0 m" },
                { label: "Yükseklik", val: "14.0 m" },
              ].map((d, i) => (
                <div key={d.label} className={`px-4 py-3 ${i < 2 ? "border-r border-[#E8E8E8]" : ""}`}>
                  <p className="text-[9px] text-[#9A9A9A] uppercase tracking-widest font-mono">{d.label}</p>
                  <p className="text-sm font-bold text-[#2B2B2B] mt-1">{d.val}</p>
                </div>
              ))}
            </div>

            {/* Legend */}
            <div className="mt-4 flex flex-wrap gap-4">
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 bg-[#FFF0E6] border border-[#A66A3F]" />
                <span className="text-[10px] text-[#6A6A6A]">Batarya Odası</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 bg-[#F0F0F0] border border-[#2B2B2B]" />
                <span className="text-[10px] text-[#6A6A6A]">Teknik Alan</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 bg-white border border-[#CCCCCC]" />
                <span className="text-[10px] text-[#6A6A6A]">Genişleme Alanı</span>
              </div>
            </div>
          </div>

          {/* RIGHT — Active room detail */}
          <div
            style={{
              opacity: visible ? 1 : 0,
              transform: visible ? "translateX(0)" : "translateX(24px)",
              transition: "opacity 0.8s ease 0.35s, transform 0.8s ease 0.35s",
            }}
          >
            {/* Room selector tabs */}
            <div className="flex flex-wrap gap-2 mb-8">
              {rooms.map((r) => (
                <button
                  key={r.id}
                  onClick={() => setActiveRoom(r.id)}
                  className="px-3 py-1.5 text-[10px] font-semibold tracking-[0.12em] uppercase transition-all duration-200 cursor-pointer whitespace-nowrap"
                  style={{
                    background: activeRoom === r.id ? r.color : "transparent",
                    color: activeRoom === r.id ? "#fff" : "#9A9A9A",
                    border: `1px solid ${activeRoom === r.id ? r.color : "#E0E0E0"}`,
                  }}
                >
                  {r.title}
                </button>
              ))}
            </div>

            {/* Active room card */}
            <div
              key={activeRoom}
              className="border border-[#E8E8E8] p-7 md:p-8"
              style={{
                borderLeft: `4px solid ${activeData.color}`,
                animation: "fadeSlideIn 0.35s ease",
              }}
            >
              <div className="flex items-start gap-4 mb-6">
                <div
                  className="w-12 h-12 flex items-center justify-center flex-shrink-0"
                  style={{ background: activeData.bg }}
                >
                  <i className={`${activeData.icon} text-xl`} style={{ color: activeData.color }} />
                </div>
                <div>
                  <p className="text-[10px] font-semibold tracking-[0.18em] uppercase mb-1" style={{ color: activeData.color }}>
                    {activeData.subtitle}
                  </p>
                  <h3 className="text-xl font-bold text-[#2B2B2B]">{activeData.title}</h3>
                </div>
                <div className="ml-auto text-right">
                  <p className="text-[9px] text-[#9A9A9A] uppercase tracking-widest font-mono">Alan</p>
                  <p className="text-lg font-bold text-[#2B2B2B] mt-0.5">{activeData.area}</p>
                </div>
              </div>

              <p className="text-sm text-[#6A6A6A] leading-relaxed mb-7">
                {activeData.desc}
              </p>

              {/* Specs */}
              <div className="space-y-2.5">
                <p className="text-[10px] font-semibold tracking-[0.15em] uppercase text-[#9A9A9A] mb-3">
                  Teknik Özellikler
                </p>
                {activeData.specs.map((spec) => (
                  <div key={spec} className="flex items-center gap-3">
                    <div className="w-1 h-1 rounded-full flex-shrink-0" style={{ background: activeData.color }} />
                    <span className="text-sm text-[#2B2B2B]">{spec}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* All rooms summary list */}
            <div className="mt-6 space-y-2">
              {rooms.filter((r) => r.id !== activeRoom).map((r) => (
                <button
                  key={r.id}
                  onClick={() => setActiveRoom(r.id)}
                  className="w-full flex items-center gap-4 px-4 py-3 border border-[#F0F0F0] hover:border-[#E0E0E0] hover:bg-[#FAFAFA] transition-all duration-150 cursor-pointer text-left"
                >
                  <div className="w-7 h-7 flex items-center justify-center flex-shrink-0">
                    <i className={`${r.icon} text-base`} style={{ color: r.color }} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-xs font-semibold text-[#2B2B2B]">{r.title}</p>
                    <p className="text-[10px] text-[#9A9A9A] mt-0.5">{r.area}</p>
                  </div>
                  <i className="ri-arrow-right-s-line text-[#CCCCCC]" />
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>

      <style>{`
        @keyframes fadeSlideIn {
          from { opacity: 0; transform: translateY(8px); }
          to { opacity: 1; transform: translateY(0); }
        }
      `}</style>
    </section>
  );
}
